import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import {
  Controller,
  type UseFormReturn,
} from 'react-hook-form'
import { useFormWatch } from '@/utils/hooks/form-hooks'
import type {
  AbsenceImportFormValues,
  AnalyzePayload,
  ExecutePayload,
} from '../types'

interface Props {
  form: UseFormReturn<AbsenceImportFormValues>
  analysis: AnalyzePayload
  executeResult: ExecutePayload | null
  failureReport: string
  executing: boolean
  missingTypeMappingsCount: number
  onExecute(): void
  onBack(): void
  onReset(): void
  onCopyFailures(): void
  onDownloadFailures(): void
}

export function StepSummary({
  form,
  analysis,
  executeResult,
  failureReport,
  executing,
  missingTypeMappingsCount,
  onExecute,
  onBack,
  onReset,
  onCopyFailures,
  onDownloadFailures,
}: Props) {
  const translations = useTranslate({
    setApproved: 'settings.absence.import.set-approved',
    updateBalance: 'settings.absence.import.update-balance',
    lockWarning: 'settings.absence.import.lock-warning',
    importAnyway: 'settings.absence.import.import-anyway',
    import: 'settings.absence.import.import',
    back: 'common.back',
    reset: 'common.reset',
    result: 'settings.absence.import.result',
    created: 'settings.absence.import.created',
    skipped: 'settings.absence.import.skipped',
    failed: 'settings.absence.import.failed',
    failureReport: 'settings.absence.import.failure-report',
    copyFailures: 'settings.absence.import.copy-failures',
    downloadFailures: 'settings.absence.import.download-failures',
  })
  const importAnyway = useFormWatch(form, 'options.importAnyway')

  return (
    <div className="grid gap-4 rounded-md border border-stone-200 p-3">
      <div className="grid gap-2 text-sm">
        <Controller
          control={form.control}
          name="options.setApproved"
          render={({ field }) => (
            <label className="flex items-center gap-2">
              <Checkbox
                size="sm"
                value={field.value}
                onChange={checked => field.onChange(Boolean(checked))}
              />
              <span>{translations.setApproved}</span>
            </label>
          )}
        />

        <Controller
          control={form.control}
          name="options.updateBalance"
          render={({ field }) => (
            <label className="flex items-center gap-2">
              <Checkbox
                size="sm"
                value={field.value}
                onChange={checked => field.onChange(Boolean(checked))}
              />
              <span>{translations.updateBalance}</span>
            </label>
          )}
        />

        {analysis.createLockActive && (
          <>
            <p className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
              {translations.lockWarning}
            </p>

            <Controller
              control={form.control}
              name="options.importAnyway"
              render={({ field }) => (
                <label className="flex items-center gap-2">
                  <Checkbox
                    size="sm"
                    value={field.value}
                    onChange={checked => field.onChange(Boolean(checked))}
                  />
                  <span>{translations.importAnyway}</span>
                </label>
              )}
            />
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onReset}>
          {translations.reset}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          disabled={analysis.importedAbsenceTypes.length === 0}
        >
          {translations.back}
        </Button>

        <Button
          size="sm"
          disabled={
            executing ||
            missingTypeMappingsCount > 0 ||
            (analysis.createLockActive && !importAnyway)
          }
          onClick={onExecute}
        >
          {translations.import}
        </Button>
      </div>

      {executeResult && (
        <div className="grid gap-3 rounded-md border border-stone-200 p-3">
          <p className="text-sm font-medium">{translations.result}</p>

          <div className="grid gap-1 text-sm text-stone-700 md:grid-cols-3">
            <p>
              {translations.created}: {executeResult.createdCount}
            </p>
            <p>
              {translations.skipped}: {executeResult.skippedCount}
            </p>
            <p>
              {translations.failed}: {executeResult.failedCount}
            </p>
          </div>

          {!!failureReport && (
            <>
              <p className="text-sm font-medium">{translations.failureReport}</p>

              <textarea
                className="h-40 w-full rounded border border-stone-300 p-2 text-xs"
                readOnly
                value={failureReport}
              />

              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={onCopyFailures}>
                  {translations.copyFailures}
                </Button>

                <Button size="sm" variant="outline" onClick={onDownloadFailures}>
                  {translations.downloadFailures}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
